import React, { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import {
	ModuleGroups,
	PageModuleGroupModules,
	Session,
	SessionTabsLayoutTypes,
	SessionTypesEnum,
	TranslateString,
} from '../../../../../types/working-model';
import { setActiveSessionGroupModule } from '../../../../../store/actions/admin/create-event/session';
import { useTypedSelector } from '../../../../../store/reducers/use-typed-selector';
import Icon, {  } from '../../../../general-ui/icon';
import useTransformGroupModules from '../../../../../utils/use-transform-group-modules';
import { useTranslate } from '../../../../../i18n/useTranslationModules';
import useTranslationRoute from '../../../../live-event/hooks/use-translation-route';

import './editor-main.scss';
import './session-main-below-the-fold.scss';

import { useIsAboveTheFold, useIsBelowTheFold } from '../../../../../hooks/session.hooks';
import { EngageCount } from './editor-main';


const GroupedNavButtonsEditor: React.FC<{
	moduleGroupsRefs: React.RefObject<HTMLParagraphElement>[];
	autoScroll: boolean
	setAutoScroll: Dispatch<SetStateAction<boolean>>

}> = ({
	moduleGroupsRefs,
	autoScroll,
	setAutoScroll }) => {
	const workingSession = useTypedSelector(state => state.CreateSessionReducer.workingSession);
	const selectedPreviewGroupUuid = useTypedSelector(state => state.CreateSessionReducer.selectedPreviewGroupUuid);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);

	const dispatch = useDispatch();
	const namespace = useTranslationRoute();
	const { t } = useTranslate(["session", namespace]);
	const { language }: any = useParams();
	const isSessionDetailsBelowTheFoldV2 = useIsBelowTheFold(workingSession);
	const isSessionDetailsAboveTheFoldV2 = useIsAboveTheFold(workingSession);

	const workingEventSessions = workingEvent?.sessions;
	const workingEventCurrentSession = workingEventSessions?.find((eventSession: Session) => eventSession.session === workingSession?.session);
	const workingEventCurrentSessionModuleGrouping = workingEventCurrentSession?.module_grouping;

	useTransformGroupModules();


	// When we autoscroll, we don't want the tabs to auto switch when starting in a different section than the selected section
	useEffect(() => {
		setTimeout(()=>{
			if (autoScroll) {	
				setAutoScroll(false);
			}
		},1000);
	}, [autoScroll, setAutoScroll]);


	const layout: SessionTabsLayoutTypes = workingSession?.layout?.tabs?.layout || SessionTabsLayoutTypes.Horizontal;

	const showIcons = workingSession?.layout?.tabs?.show_icons ?? false;

	const groupedNavButtons = useMemo(() => {
		// create ordered lists for each group
		if (!workingSession?.module_grouping) return null;
		return workingSession?.module_grouping?.map((moduleGroup, index) => {
			// if (!activeModules.length) return <></>;

			const navigate = (moduleGroup: PageModuleGroupModules) => () => dispatch(setActiveSessionGroupModule(moduleGroup.uuid));
			
			const belowTheFoldNavigate = (moduleGroup: PageModuleGroupModules, idName: string, index: number) => () => {
				const container = moduleGroupsRefs[index];
				dispatch(setActiveSessionGroupModule(moduleGroup.uuid));

				if (container.current) {
					setAutoScroll(true);
					container.current?.scrollIntoView({ behavior: "smooth", block: "center"  });
				}
			};
			// do not show breakout tab if session is not breakout
			if (moduleGroup.type === 'breakout' && workingSession.session_type !== SessionTypesEnum.breakoutRooms) return null;
			// Module breakout rooms is the only one that doesn't have sub modules which is why we have this condition
			if (!moduleGroup.is_on || (!moduleGroup.modules.length && (moduleGroup.type !== "breakout"))) return null;
			const { uuid, icon, name } = moduleGroup;

			let _name: string;
			//Check to see if "name" is a Translate string, if so return the translated value, if not, then it is already a string
			const stringName = name as ModuleGroups;
			const translateName = name as TranslateString;
			if (typeof translateName === 'object') {
				let moduleTranslateString = '';
				const moduleGrouping = workingEventCurrentSessionModuleGrouping
					? workingEventCurrentSessionModuleGrouping[index]?.name as TranslateString
					: null;
				if (moduleGrouping) {
					moduleTranslateString = moduleGrouping[language] as string;
				}
				// final fallback is for sessions created with a default language that was not one of the session languages (before that got fixed)
				_name = t(`${namespace}:tabName.${translateName.base}`, moduleTranslateString
					?? translateName[language] as string
					?? translateName.base
					?? Object.values(translateName)[0]);
			} else {
				_name = t(stringName) ?? stringName;
			}

			return (
				<li
					key={uuid}
					className={classNames({
						active: selectedPreviewGroupUuid
							? uuid === selectedPreviewGroupUuid
							: index === 0
					})}
				>
					{isSessionDetailsBelowTheFoldV2 || isSessionDetailsAboveTheFoldV2 ?
						<button
							onClick={belowTheFoldNavigate(moduleGroup, _name, index)}
							className={classNames('clear no-style')}

						>
							<span>{_name}</span>
							<EngageCount group={moduleGroup} session={workingSession} />
						</button> : 
						<button
							onClick={navigate(moduleGroup)}
							className={classNames(
								'clear no-style',
								{
									'sharp': [SessionTabsLayoutTypes.Horizontal, SessionTabsLayoutTypes.Vertical].includes(layout),
								}
							)}
						>
							<Icon name={icon || ""} size={16} color={classNames({ noIcon: !showIcons })} />
							<span>{_name}</span>
						</button> 

					}
				</li >
			);
		});
	}, [workingSession, selectedPreviewGroupUuid, isSessionDetailsBelowTheFoldV2, isSessionDetailsAboveTheFoldV2, layout, showIcons, dispatch, moduleGroupsRefs, setAutoScroll, workingEventCurrentSessionModuleGrouping, t, namespace, language]);

	
	return <>{groupedNavButtons}</>;
	
};

export default GroupedNavButtonsEditor;